import React from "react";
import { useQuery } from "@tanstack/react-query";
import api, { API_BASE } from "@/lib/api";
import { openFile } from "@/lib/fileViewer";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { FileText, FileIcon, Download, FolderOpen } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { SharedDocsList } from "@/components/documents/SharedDocsList";
import { StudentUploadButton } from "@/components/documents/StudentUploadButton";

function iconFor(ext) {
  if (ext === "pdf") return <FileText size={18} className="text-red-600" />;
  if (["png", "jpg", "jpeg", "webp"].includes(ext))
    return <FileIcon size={18} className="text-emerald-700" />;
  return <FileIcon size={18} className="text-stone-500" />;
}

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function PortalDocumenti() {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["portal-my-documents"],
    queryFn: async () => (await api.get("/students/me/documents")).data,
  });

  function openDoc(doc) {
    openFile(`${API_BASE}/students/documents/${doc.id}/download?auth=${encodeURIComponent(token || "")}`, doc.original_filename);
  }

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1000px] mx-auto" data-testid="portal-documenti">
      <PageHeader
        title="Documenti"
        subtitle="Regolamenti e documenti per tutti, i tuoi documenti personali e i file che carichi tu."
      />

      <div className="mb-10" data-testid="portal-shared-docs">
        <h2 className="text-base md:text-lg font-semibold text-[#1C352D] mb-3">Per tutti</h2>
        <SharedDocsList />
      </div>

      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="text-base md:text-lg font-semibold text-[#1C352D]">I miei documenti</h2>
        <StudentUploadButton />
      </div>

      {isLoading ? (
        <div className="text-sm text-stone-400 text-center py-8">Caricamento…</div>
      ) : docs.length === 0 ? (
        <Card className="p-10 text-center text-sm text-stone-400 border border-stone-200 rounded-xl shadow-none">
          <FolderOpen size={28} className="mx-auto mb-3 text-stone-300" />
          Non hai ancora documenti personali.
        </Card>
      ) : (
        <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
          <div className="divide-y divide-stone-100">
            {docs.map((d) => (
              <button
                key={d.id}
                onClick={() => openDoc(d)}
                data-testid={`portal-doc-${d.id}`}
                className="w-full p-4 flex items-center gap-3 hover:bg-stone-50/60 transition-colors duration-150 text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-stone-50 border border-stone-200 grid place-items-center shrink-0">
                  {iconFor(d.extension)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#1C352D] truncate">{d.label}{d.uploaded_by_student && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 font-normal">Caricato da te</span>}</div>
                  <div className="text-[11px] text-stone-500 truncate mt-0.5">
                    {d.original_filename} · {formatSize(d.size)} · caricato {fmtDate(d.uploaded_at)}
                  </div>
                </div>
                <Download size={16} className="text-stone-400 shrink-0" />
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
